import type { AiSettings } from '../types';
import Toggle from './Toggle';

interface Props {
  form: AiSettings;
  set: <K extends keyof AiSettings>(key: K, val: AiSettings[K]) => void;
}

interface GoalCardProps {
  icon: string;
  title: string;
  desc: string;
  badge?: string;
  enabled: boolean;
  comingSoon?: boolean;
  onChange: (v: boolean) => void;
}

function GoalCard({ icon, title, desc, badge, enabled, comingSoon, onChange }: GoalCardProps) {
  return (
    <div
      className={`rounded-xl border p-5 transition-all ${
        enabled && !comingSoon
          ? 'border-[#C8A96E]/40 bg-[#C8A96E]/5'
          : 'border-slate-200 bg-white'
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div
            className="w-10 h-10 flex items-center justify-center rounded-xl shrink-0"
            style={{
              backgroundColor:
                enabled && !comingSoon ? 'rgba(200,169,110,0.15)' : '#f1f5f9',
            }}
          >
            <i
              className={`${icon} text-lg`}
              style={{ color: enabled && !comingSoon ? '#C8A96E' : '#cbd5e1' }}
            />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-[#0f172a] font-semibold text-sm">{title}</p>
              {badge && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-400">
                  {badge}
                </span>
              )}
              {comingSoon && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-500 border border-amber-200">
                  Coming soon
                </span>
              )}
            </div>
            <p className="text-slate-400 text-xs mt-0.5 leading-relaxed max-w-sm">{desc}</p>
          </div>
        </div>
        <Toggle enabled={enabled} onChange={onChange} disabled={comingSoon} />
      </div>
    </div>
  );
}

export default function GoalsTab({ form, set }: Props) {
  return (
    <div className="space-y-5">

      <div className="flex items-start gap-3 px-4 py-3.5 bg-[#C8A96E]/8 rounded-xl border border-[#C8A96E]/25">
        <i className="ri-focus-3-line text-[#C8A96E] text-sm mt-0.5 shrink-0" />
        <p className="text-[#0f172a]/65 text-xs leading-relaxed">
          Goals define what the assistant should try to accomplish during each conversation.
          Enable the ones that match your business workflow.
        </p>
      </div>

      {/* Appointment booking goal */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <h3 className="text-[#0f172a] font-semibold text-sm flex items-center gap-2">
          <span className="w-6 h-6 flex items-center justify-center">
            <i className="ri-flag-line text-[#C8A96E]" />
          </span>
          Conversion Goals
        </h3>
        <div className="space-y-3">
          <GoalCard
            icon="ri-calendar-check-line"
            title="Push to Book Appointment"
            desc="The assistant will guide conversations toward scheduling a consultation or session booking. Requires Appointments module."
            badge="Appointments"
            enabled={form.appointment_goal_enabled}
            onChange={(v) => set('appointment_goal_enabled', v)}
          />
          <GoalCard
            icon="ri-whatsapp-line"
            title="WhatsApp Handoff"
            desc="When a prospect is interested, the assistant offers to continue the conversation on WhatsApp for a more personal touch."
            badge="WhatsApp"
            enabled={form.whatsapp_handoff_enabled}
            onChange={(v) => set('whatsapp_handoff_enabled', v)}
          />
        </div>
      </div>

      {/* Future goals */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <h3 className="text-[#0f172a] font-semibold text-sm flex items-center gap-2">
          <span className="w-6 h-6 flex items-center justify-center">
            <i className="ri-time-line text-slate-300" />
          </span>
          Upcoming Goals
        </h3>
        <div className="space-y-3">
          <GoalCard
            icon="ri-send-plane-line"
            title="Auto-send Quote"
            desc="Automatically generate and send a quote PDF based on the conversation context."
            enabled={false}
            comingSoon
            onChange={() => {}}
          />
          <GoalCard
            icon="ri-coupon-line"
            title="Promo Offer"
            desc="Offer a limited-time discount or promotion to prospects showing intent to book."
            enabled={false}
            comingSoon
            onChange={() => {}}
          />
          <GoalCard
            icon="ri-mail-line"
            title="Email Follow-up"
            desc="Trigger an automated follow-up email sequence if the prospect doesn't book."
            enabled={false}
            comingSoon
            onChange={() => {}}
          />
        </div>
      </div>

      {/* Summary */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="text-[#0f172a] font-semibold text-sm mb-3">Active Goals Summary</h3>
        <div className="grid grid-cols-2 gap-3">
          <div
            className={`rounded-lg px-4 py-3 border text-center ${
              form.appointment_goal_enabled
                ? 'bg-emerald-50 border-emerald-200'
                : 'bg-slate-50 border-slate-200'
            }`}
          >
            <i
              className={`ri-calendar-check-line text-xl mb-1 block ${
                form.appointment_goal_enabled ? 'text-emerald-500' : 'text-slate-300'
              }`}
            />
            <p
              className={`text-xs font-medium ${
                form.appointment_goal_enabled ? 'text-emerald-700' : 'text-slate-400'
              }`}
            >
              Booking Push
            </p>
            <p
              className={`text-[10px] mt-0.5 ${
                form.appointment_goal_enabled ? 'text-emerald-500' : 'text-slate-300'
              }`}
            >
              {form.appointment_goal_enabled ? 'Active' : 'Inactive'}
            </p>
          </div>
          <div
            className={`rounded-lg px-4 py-3 border text-center ${
              form.whatsapp_handoff_enabled
                ? 'bg-emerald-50 border-emerald-200'
                : 'bg-slate-50 border-slate-200'
            }`}
          >
            <i
              className={`ri-whatsapp-line text-xl mb-1 block ${
                form.whatsapp_handoff_enabled ? 'text-emerald-500' : 'text-slate-300'
              }`}
            />
            <p
              className={`text-xs font-medium ${
                form.whatsapp_handoff_enabled ? 'text-emerald-700' : 'text-slate-400'
              }`}
            >
              WhatsApp Handoff
            </p>
            <p
              className={`text-[10px] mt-0.5 ${
                form.whatsapp_handoff_enabled ? 'text-emerald-500' : 'text-slate-300'
              }`}
            >
              {form.whatsapp_handoff_enabled ? 'Active' : 'Inactive'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}